import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Chip,
  Typography,
  Stack,
  Alert,
  Skeleton,
  alpha,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Task as TaskIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { RTLDialogTitle, RTLDialogContent, RTLDialogActions } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Custom hooks
import { useTaskOperations } from '../../hooks/useTaskOperations';
import { useTaskData } from '../../hooks/useTaskData';

// Components
import { DocumentDisplay } from './DocumentDisplay';
import { ActivityTimeline } from './ActivityTimeline';
import { DocumentReceiptActions, TaskCompletionActions } from './TaskActions';

// Utils
import {
  canMarkAsReceived,
  canProcessDocument,
  isInformationalOnly,
  isCurrentUserAssignee,
  getEffectiveStatus,
  useTaskTranslations
} from '../../utils/taskUtils';
import { getSafeColor } from '../../utils/colorUtils';

const TaskDetailDialog = ({ 
  task, 
  open, 
  onClose, 
  onSuccess 
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(['tasks', 'documents', 'common']);
  const { isRTL, getRTLProps } = useRTL();
  const { user } = useAuth();
  const { getTaskStatusLabel } = useTaskTranslations();

  // Custom hooks for data management
  const {
    documentDetails,
    documentHistory,
    users,
    departments,
    loading: dataLoading,
    errors: dataErrors
  } = useTaskData(task);
  
  // Debug: Log task and document information
  React.useEffect(() => {
    if (task) {
      console.log('=== TaskDetailDialogV2 Debug ===');
      console.log('Task object:', task);
      console.log('Task document:', task.document);
      console.log('Task document_id:', task.document_id);
      console.log('Document details from useTaskData:', documentDetails);
    }
  }, [task, documentDetails]);

  // Custom hook for task operations
  const {
    markReceived,
    rejectReceipt,
    completeTask,
    rejectTask,
    forwardTask,
    loading: operationLoading,
    error: operationError,
    clearError
  } = useTaskOperations((updatedTask, action) => {
    // Handle successful operations
    onSuccess?.(updatedTask, action);
    if (['received', 'completed', 'rejected', 'forwarded'].includes(action)) {
      onClose();
    }
  });

  // Permission checks
  const isUserAssignee = isCurrentUserAssignee(task, user);
  const isInfoOnly = isInformationalOnly(task);
  const canReceive = canMarkAsReceived(task, user);
  const canProcess = canProcessDocument(task, user);

  const handleClose = () => {
    clearError();
    onClose();
  };

  const handleForward = (forwardData) => {
    forwardTask(task, forwardData);
  };

  if (!task) return null;

  const effectiveStatus = getEffectiveStatus(task);
  const statusColor = getSafeColor(effectiveStatus);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      {/* Dialog Header */}
      <RTLDialogTitle
        {...getRTLProps('dialogtitle')}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          px: 3,
          py: 2.5
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main'
            }}>
              <TaskIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mr: 1 }}>
                {t('tasks:task_details')}
              </Typography>
              {/* <Typography variant="caption" color="text.secondary">
                {t('tasks:task_id')}: {task.id}
              </Typography> */}
            </Box>
          </Stack>
          
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="medium"
              label={getTaskStatusLabel(effectiveStatus)}
              color={statusColor}
              sx={{ fontWeight: 600 }}
            />
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </RTLDialogTitle>

      {/* Dialog Content */}
      <RTLDialogContent {...getRTLProps('dialogcontent')} dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Global Error Display */}
          {operationError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
              {operationError}
            </Alert>
          )}

          {/* Informational Notice */}
          {isInfoOnly && (
            <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
              {t('tasks:informational_only_notice', 'This task is informational only. Only completion is required; other actions are disabled.')}
            </Alert>
          )}

          {/* Document Display */}
          <DocumentDisplay 
            document={documentDetails}
            loading={dataLoading.document}
            error={dataErrors.document}
            task={task}
          />

          {/* Task Actions */}
          {isUserAssignee && (
            <Box sx={{ mb: 4 }}>
              {/* Document Receipt Actions */}
              {canReceive && (
                <DocumentReceiptActions
                  onReceive={() => markReceived(task)}
                  onRejectReceipt={(reason) => rejectReceipt(task, reason)}
                  loading={operationLoading}
                  error={operationError}
                />
              )}

              {/* Task Completion Actions */}
              {canProcess && !canReceive && (
                <TaskCompletionActions
                  onComplete={(notes) => completeTask(task, notes)}
                  onReject={(reason) => rejectTask(task, reason)}
                  onForward={handleForward}
                  loading={operationLoading}
                  error={operationError}
                  users={users}
                  departments={departments}
                  isInformationalOnly={isInfoOnly}
                />
              )}
            </Box>
          )}

          {/* Activity Timeline */}
          <ActivityTimeline
            activities={documentHistory}
            loading={dataLoading.history}
            error={dataErrors.history}
            task={task}
          />

          {/* Forwarding Chain Information */}
          {task.metadata?.forwardChain && task.metadata.forwardChain.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('tasks:forwarding_chain')}:
              </Typography>
              <Box sx={{ 
                pl: isRTL ? 0 : 2, 
                pr: isRTL ? 2 : 0,
                borderLeft: isRTL ? 0 : 2, 
                borderRight: isRTL ? 2 : 0,
                borderColor: 'divider' 
              }}>
                {task.metadata.forwardChain.map((forward, index) => (
                  <Box key={index} sx={{ 
                    mb: 1.5, 
                    p: 1.5, 
                    bgcolor: 'background.default', 
                    borderRadius: 1,
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    <Typography variant="body2" fontWeight="medium">
                      {forward.forwarded_by_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tasks:forwarded_on')}: {new Date(forward.forwarded_at).toLocaleString()}
                    </Typography>
                    {forward.forwarded_to_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('tasks:forwarded_to')}: {forward.forwarded_to_name}
                      </Typography>
                    )}
                    {forward.notes && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('tasks:reason')}: {forward.notes}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Forwarded Task Completion */}
          {task.status === 'FORWARDED' && task.metadata?.forwardedCompletion && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight="medium">
                {t('tasks:forwarded_task_completed')}
              </Typography>
              <Typography variant="caption" display="block">
                {t('tasks:completed_on')}: {new Date(task.metadata.forwardedCompletion.completedAt).toLocaleString()}
              </Typography>
              {task.metadata.forwardedCompletion.completedBy && (
                <Typography variant="caption" display="block">
                  {t('tasks:completed_by')}: {task.metadata.forwardedCompletion.completedBy}
                </Typography>
              )}
              {task.metadata.forwardedCompletion.reason && (
                <Typography variant="caption" display="block">
                  {t('tasks:completion_reason')}: {task.metadata.forwardedCompletion.reason}
                </Typography>
              )}
            </Alert>
          )}
        </Box>
      </RTLDialogContent>

      {/* Dialog Actions */}
      <RTLDialogActions {...getRTLProps('dialogactions')} sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          size="large"
          sx={{ minWidth: 120 }}
        >
          {t('common:close')}
        </Button>
      </RTLDialogActions>
    </Dialog>
  );
};

export default TaskDetailDialog; 